import { useOverlayTriggerRefocusGuard } from '@heidi/utils';
import {
  type MutableRefObject,
  type PointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

/** How long a sustained mouse hover on the trigger takes to open the menu
 * on its own (MUL-3521) — long enough that a cursor passing over the
 * switcher on its way elsewhere doesn't pop it open.
 *
 * Not exported: the menu's suite keeps its own copy of this value. */
const HOVER_OPEN_DELAY_MS = 500;

/** Grace period after the pointer leaves the trigger or the content before a
 * hover-opened menu closes — long enough to bridge the gap while the cursor
 * travels from the trigger to the content (or back), short enough that
 * genuinely moving away closes it promptly. Only a HOVER-opened menu closes
 * this way; a click-opened menu stays open until a click-outside/Escape,
 * same as any other dropdown. Not exported — see `HOVER_OPEN_DELAY_MS`. */
const HOVER_CLOSE_DELAY_MS = 200;

const clearTimer = (ref: MutableRefObject<number | null>) => {
  if (ref.current !== null) {
    window.clearTimeout(ref.current);
    ref.current = null;
  }
};

/**
 * The workspace menu's hover-open / click-lock / hover-close dwell,
 * split out of the menu so the component stays a screen. Returns the open
 * state, Radix's `onOpenChange`, the trigger refocus guard, and the pointer
 * and focus handlers for the trigger and the content.
 */
export function useHoverDwellMenu() {
  const [menuOpen, setMenuOpen] = useState(false);
  // Pinned once per open-session to how THIS open began, not read live from
  // `openedViaHoverRef` — see the comment on `<DropdownMenu>` in the menu for
  // why modal can't just track that ref's live value.
  const [isModal, setIsModal] = useState(true);
  // MUL-3522 follow-up: without this, Radix's close-time trigger refocus
  // leaves the avatar's `:focus-visible` ring stuck on after a mouse user
  // opens the menu and clicks out. Called here, right after the open state,
  // so the hover close below can arm it.
  const refocusGuard = useOverlayTriggerRefocusGuard(menuOpen);

  const hoverTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const suppressResetTimerRef = useRef<number | null>(null);
  // True while the open menu was opened by hover rather than a click — closes
  // on pointer-leave below. A trigger click always clears it and keeps the
  // menu open ("locked", like a normal click-opened dropdown, MUL-3521).
  const openedViaHoverRef = useRef(false);
  // Swallows every Radix-driven close for one tick after a click on an
  // already-open trigger: Radix's own toggle AND its separate focus-outside
  // dismiss (the click also focuses the trigger, which sits outside the
  // content) both fire for the same click, not just the toggle. A fresh
  // pointerdown always clears this first (below) before maybe re-arming it —
  // otherwise a second, genuine click landing inside that window could have
  // ITS OWN legitimate close swallowed by the first click's leftover flag.
  const suppressNextCloseRef = useRef(false);
  // What had focus right before a hover-open, restored below once Radix's
  // FocusScope has auto-focused the content — a hover-opened menu shouldn't
  // steal focus off whatever the user was doing, since they only hovered and
  // didn't ask to interact with it yet. `DropdownMenuContent`'s
  // `onOpenAutoFocus` can't do this directly: Radix keeps that prop private to
  // its own modal/non-modal Content variants, not part of the public type.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  // True only for the synchronous span of the restore's own `target.focus()`
  // call below — lets the content's `onFocusOutside` tell OUR restore apart
  // from a real outside focus (e.g. Tab) landing on the same element, since
  // Radix's non-modal content dismisses on any focus-outside and does not
  // otherwise distinguish the two.
  const restoringFocusRef = useRef(false);
  useEffect(() => {
    if (!menuOpen || !openedViaHoverRef.current) return;
    // Deferred a macrotask rather than run inline: FocusScope's OWN
    // mount-auto-focus effect (a sibling/descendant effect in this same
    // commit) runs its `focusFirst`/fallback-to-container steal AFTER this
    // effect in practice — restoring inline here just loses the race and the
    // container keeps focus. A macrotask runs once every effect from this
    // commit has already flushed, so it always wins.
    const target = previouslyFocusedRef.current;
    const timeoutId = window.setTimeout(() => {
      // `focus()` dispatches `focusin` synchronously, so a flag held only
      // around the call is enough for `onFocusOutside` below to see it.
      restoringFocusRef.current = true;
      target?.focus();
      restoringFocusRef.current = false;
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [menuOpen]);
  const scheduleHoverClose = () => {
    clearTimer(closeTimerRef);
    closeTimerRef.current = window.setTimeout(() => {
      // A hover close is a pointer-driven dismiss like a click-outside, so it
      // shouldn't refocus the trigger either (MUL-3522's stuck focus ring) —
      // and leaving the trigger focused is exactly what let the NEXT hover's
      // focus-restore above dismiss itself.
      refocusGuard.suppressNextRefocus();
      setMenuOpen(false);
      openedViaHoverRef.current = false;
    }, HOVER_CLOSE_DELAY_MS);
  };
  const handleOpenChange = (next: boolean) => {
    if (!next && suppressNextCloseRef.current) return;
    // Any Radix-driven open/close request means this transition didn't come
    // from the hover path (hover sets `menuOpen` directly, bypassing this
    // function) — clear a still-pending hover timer so it can't fire later
    // and mislabel a keyboard- or click-opened menu as hover-opened, which
    // would then wrongly auto-close it on the next pointer-leave.
    clearTimer(hoverTimerRef);
    if (next) {
      setIsModal(true);
    } else {
      openedViaHoverRef.current = false;
      clearTimer(closeTimerRef);
    }
    setMenuOpen(next);
  };
  const closeMenu = () => {
    clearTimer(hoverTimerRef);
    clearTimer(closeTimerRef);
    openedViaHoverRef.current = false;
    setMenuOpen(false);
  };
  useEffect(
    () => () => {
      clearTimer(hoverTimerRef);
      clearTimer(closeTimerRef);
      clearTimer(suppressResetTimerRef);
    },
    []
  );

  const triggerProps = {
    onPointerEnter: (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      clearTimer(closeTimerRef);
      if (menuOpen) return;
      clearTimer(hoverTimerRef);
      hoverTimerRef.current = window.setTimeout(() => {
        previouslyFocusedRef.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setMenuOpen(true);
        setIsModal(false);
        openedViaHoverRef.current = true;
      }, HOVER_OPEN_DELAY_MS);
    },
    onPointerLeave: () => {
      clearTimer(hoverTimerRef);
      if (openedViaHoverRef.current) scheduleHoverClose();
    },
    onPointerDown: () => {
      clearTimer(hoverTimerRef);
      clearTimer(closeTimerRef);
      // A new pointerdown always invalidates a PREVIOUS click's leftover
      // suppression before maybe arming its own — see the comment on
      // `suppressNextCloseRef` above.
      clearTimer(suppressResetTimerRef);
      suppressNextCloseRef.current = false;
      // Only a HOVER-opened menu gets "locked" into staying open by this
      // click — a menu already opened by a previous click keeps Radix's
      // normal toggle, so a second click/tap still closes it.
      if (menuOpen && openedViaHoverRef.current) {
        suppressNextCloseRef.current = true;
        suppressResetTimerRef.current = window.setTimeout(() => {
          suppressNextCloseRef.current = false;
        }, 0);
      }
      openedViaHoverRef.current = false;
    },
  };

  const contentProps = {
    onPointerEnter: () => clearTimer(closeTimerRef),
    onPointerLeave: () => {
      if (openedViaHoverRef.current) scheduleHoverClose();
    },
    // Only swallow the dismissal for OUR OWN restore (above) — a keyboard user
    // tabbing away from a hover-opened menu should still be able to dismiss it
    // this way.
    onFocusOutside: (event: Event) => {
      if (restoringFocusRef.current) event.preventDefault();
    },
  };

  return {
    menuOpen,
    isModal,
    handleOpenChange,
    closeMenu,
    refocusGuard,
    triggerProps,
    contentProps,
  };
}
